import Handlebars from '@sparta.global.vendors/handlebars';
import breakOut from '@sparta.global.utilities/sparta-breakout-utility/dist/es6.js';
import spartaEventsUtility from '@sparta.global.utilities/sparta-events-utility/dist/es6.js';
import '@sparta.global.utilities/sparta-global-functions-utility/dist/es6.js';
import '@sparta.global.utilities/sparta-store-utility/dist/es6.js';

/**
 * Namespace for the Sparta Tab Dropdown Utility.
 * @constant {string}
 */
const utilityNamespace = 'spartaTabDropdownUtility';

/**
 * @const STRINGS
 * @description constant for storing message strings
 */
const STRINGS = {
  DROPDOWN_NO_ELM: 'Warning: Either no tab or panel object was found. Searched instance using ID: ',
  DROPDOWN_NO_DROPDOWN_OBJ: 'Warning: No dropdown object was found. Searched instance using ID: '
};

/**
 * Collection of namespaced event strings.
 * @constant {object}
 */
const EVENTS = {
  TAB_DROPDOWN_INITIALIZED: `TAB_DROPDOWN_INITIALIZED.${utilityNamespace}`};

/**
 * @class SpartaTabDropdownUtility
 * @description
 *  configure inputs for validation, formatting, obfuscation, etc.
 */
class SpartaTabDropdownUtility {
  /**
   * @method constructor
   * @description On tab click, check if content needs to be cleared and create a spinner.
   * @param {Object} settingsObj Settings object that contains tab utility information
   * @param {Object} tabAndPanelObj Object with DOM elements for tab, panel, dropdown, and settings
   * @returns {void}
   */
  init(settingsObj, tabAndPanelObj) {
    // kill check for dropdown element
    if (!tabAndPanelObj) {
      throw new Error(STRINGS.DROPDOWN_NO_ELM, tabAndPanelObj);
    }
    const tabAndPanel = tabAndPanelObj;
    const settings = settingsObj;
    const tab = tabAndPanel.tab;
    const panel = tabAndPanel.panel;
    const dropdown = tabAndPanel.dropdown;

    // Kill checks.
    if (
    // If clicked tab should be loading dynamic content
    !tab.getAttribute('data-preserve')

    // If content is already in the DOM.
    || dropdown && dropdown.body.innerHTML.trim().length && dropdown.tabPreserve === 'true') {
      return;
    }

    // Empty the DOM area.
    if (dropdown.head) {
      dropdown.head.innerHTML = '';
    }
    if (dropdown.body) {
      dropdown.body.innerHTML = '';
    }
    if (dropdown.foot) {
      dropdown.foot.innerHTML = '';
    }

    // Create and reset spinner.
    if (dropdown.spinner === true) {
      this.dropdownCreateSpinner(settings, tabAndPanel);
      this.dropdownSpinnerReset(panel, settings, tabAndPanel);
    }

    // If utility is included, invoke prerender/breakout event listener.
    if (typeof breakOut === 'object') {
      breakOut.init();
    }

    // publish tab init event
    // call via SpartaTabDropdownUtility
    spartaEventsUtility.pub(EVENTS.TAB_DROPDOWN_INITIALIZED, tabAndPanel);
  } // END constructor

  // -------------------------------------------------------- //
  // Core functions below this point
  // -------------------------------------------------------- //

  /**
   * Build loader for for dropdown
   * @param {object} settings - Object containing full list of DOM elements and class names.
   * @param {object} tabAndPanel - Object of tab, panel, and dropdown DOM elements and settings.
   * @returns {void}
   */
  dropdownCreateSpinner(settings, tabAndPanel) {
    // get dropdown content area
    const dropdown = tabAndPanel.dropdown;
    const dropdownTarget = dropdown.loader;
    if (!dropdownTarget) {
      return;
    }

    // see if loader already exist
    const loader = dropdownTarget.getElementsByClassName(settings.classNames.dropdownSpinner).length ? dropdownTarget.getElementsByClassName(settings.classNames.dropdownSpinner)[0] : null;
    if (loader) {
      return;
    }

    // get settings from DOM element
    const spinnerText = dropdown.spinnerText;

    // else create dropdown
    const spinner = document.createElement('DIV');
    spinner.classList.add(settings.classNames.dropdownSpinner);
    spinner.innerHTML = `
      <span class="
        spa-icon
        ${settings.classNames.dropdownSpinnerIcon}
        ${settings.classNames.dropdownSpinnerIconName}
        ${settings.classNames.dropdownSpinnerIconSize}
      "></span>
      ${spinnerText ? `
        <div class="${settings.classNames.dropdownSpinnerText}">${spinnerText}</div>
      ` : ''}
    `;

    // append dropdown to DOM
    dropdownTarget.insertBefore(spinner, dropdownTarget.childNodes[0]);
  }

  /**
   * Hide loader in DOM
   * @param {object} panel - DOM element that contains dropdown content/
   * @param {object} settings - Object of instance params.
   * @returns {void}
   */
  dropdownSpinnerHide(panel, settings) {
    const spinner = panel.getElementsByClassName(settings.classNames.dropdownSpinner).length ? panel.getElementsByClassName(settings.classNames.dropdownSpinner)[0] : null;
    if (spinner) {
      spinner.classList.add(settings.classNames.dropdownSpinnerHide);
    }
  }

  /**
   * Reset loader class in DOM
   * @param {object} panel - DOM element that contains dropdown content/
   * @param {object} settings - Object of instance params.
   * @returns {void}
   */
  dropdownSpinnerReset(panel, settings) {
    const spinner = panel.getElementsByClassName(settings.classNames.dropdownSpinner).length ? panel.getElementsByClassName(settings.classNames.dropdownSpinner)[0] : null;
    if (spinner) {
      spinner.classList.remove(settings.classNames.dropdownSpinnerHide);
    }
  }

  /**
   * Using promise, wait for a string to be given and place it into the DOM
   * @param {object} settingsObj - Object of instance params.
   * @param {object} tab - DOM element that toggles dropdown content.
   * @param {object} panel - DOM element that contains dropdown content.
   * @param {object | string} safeString - Either a string or object containing dropdown content.
   * @param {object} utilityParentName - Enables use of `SpartaTabNavUtility` utility functions.
   * @returns {void}
   */
  dropdownLoadContent(settingsObj, tab, panel, safeString, utilityParentName) {
    // Retain access to utility functions.
    const htmlSafeString = safeString;
    const settings = settingsObj;
    let safeStringHead;
    let safeStringBody;
    let safeStringFoot;
    let callback;

    // Kill check if clicked tab should be loading dynamic content.
    if (!tab.getAttribute('data-preserve')) {
      return;
    }

    // Pull specific panel from settings object.
    const tabAndPanel = utilityParentName.findInstance(panel.id);

    // Kill check to ensure we've found dropdown object.
    if (!tabAndPanel || !tabAndPanel.dropdown) {
      // eslint-disable-next-line no-console
      console.warn(STRINGS.DROPDOWN_NO_DROPDOWN_OBJ, panel.id);
      return;
    }

    // DOM quick references.
    const dropdownHead = tabAndPanel.dropdown.head;
    const dropdownBody = tabAndPanel.dropdown.body;
    const dropdownFoot = tabAndPanel.dropdown.foot;
    const tabPreserve = tabAndPanel.dropdown.tabPreserve;

    // Kill check if dropdown is meant to re-load content.
    if (dropdownBody && dropdownBody.innerHTML.trim().length && tabAndPanel.dropdown.tabPreserve === 'true' || panel.getAttribute('data-content-loaded') === 'true') {
      return;
    }

    // if we got an object, set variables based off of it
    if (typeof htmlSafeString === 'object') {
      safeStringHead = htmlSafeString.htmlHead ? new Handlebars.SafeString(`${htmlSafeString.htmlHead}`).string : '';
      safeStringBody = htmlSafeString.htmlBody ? new Handlebars.SafeString(`${htmlSafeString.htmlBody}`).string : '';
      safeStringFoot = htmlSafeString.htmlFoot ? new Handlebars.SafeString(`${htmlSafeString.htmlFoot}`).string : '';
      ({
        callback
      } = htmlSafeString);
    } else {
      // else we just got a string so use it
      safeStringBody = htmlSafeString;
    }

    // remove spinner
    this.dropdownSpinnerHide(panel, settings);

    // Load header content.
    if (safeStringHead && dropdownHead && !dropdownHead.innerHTML.trim().length) {
      dropdownHead.innerHTML = safeStringHead;
    }

    // Load body content.
    if (safeString && dropdownBody && !dropdownBody.innerHTML.trim().length) {
      dropdownBody.innerHTML = safeStringBody;
    }

    // Load footer content.
    if (safeStringFoot && dropdownFoot && !dropdownFoot.innerHTML.trim().length) {
      dropdownFoot.innerHTML = safeStringFoot;
    }

    // add DOM flag to dropdown
    if (tabPreserve === 'true') {
      tab.setAttribute('data-content-loaded', 'true');
      panel.setAttribute('data-content-loaded', 'true');
    }

    // Fire the callback if one was passed.
    if (typeof callback === 'function') {
      callback();
    }
  }
}

// export new instance of Sparta Tab Utility
var index = new SpartaTabDropdownUtility();

export { index as default };
